import { z } from "zod";

// ============================================================================
// Import Module Types
// ============================================================================

export const importModuleEnum = z.enum([
  "relations",
  "invoices",
  "expenses",
  "items",
  "taxes",
  "categories",
  "estimates",
  "credit-notes",
  "payments",
]);

export type ImportModule = z.infer<typeof importModuleEnum>;

// ============================================================================
// Column Mapping
// ============================================================================

export const columnTransformEnum = z.enum([
  "none",
  "date",
  "number",
  "boolean",
  "currency",
  "lookup",
]);

export type ColumnTransform = z.infer<typeof columnTransformEnum>;

export const columnMappingSchema = z.object({
  sourceColumn: z.string({ invalid_type_error: "Source column must be a string" }),
  targetField: z.string({ invalid_type_error: "Target field must be a string" }),
  transform: columnTransformEnum.optional().default("none"),
  lookupModule: z.string().optional(), // For foreign key lookups (e.g., "relations" for relationId)
});

export type ColumnMapping = z.infer<typeof columnMappingSchema>;

// ============================================================================
// Import Field Definition (for column mapping UI)
// ============================================================================

export const importFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
  required: z.boolean(),
  type: z.enum(["string", "number", "boolean", "date", "enum", "lookup"]).optional(),
  options: z.array(z.string()).optional(), // For enum fields
  lookupModule: z.string().optional(), // For lookup fields
  description: z.string().optional(),
});

export type ImportField = z.infer<typeof importFieldSchema>;

// ============================================================================
// Import Row Result
// ============================================================================

export const importErrorSchema = z.object({
  field: z.string(),
  message: z.string(),
  value: z.any().optional(),
});

export type ImportError = z.infer<typeof importErrorSchema>;

export const importRowResultSchema = z.object({
  rowIndex: z.number(),
  success: z.boolean(),
  data: z.record(z.any()).optional(), // Transformed data
  errors: z.array(importErrorSchema).optional(),
  entityId: z.string().optional(), // Created entity ID if successful
  skipped: z.boolean().optional(), // True if skipped (e.g., duplicate)
  skipReason: z.string().optional(),
});

export type ImportRowResult = z.infer<typeof importRowResultSchema>;

// ============================================================================
// Import Options
// ============================================================================

export const importOptionsSchema = z.object({
  skipDuplicates: z.boolean().optional(),
  updateExisting: z.boolean().optional(),
  dryRun: z.boolean().optional(), // Validate only, don't insert
});

export type ImportOptions = z.infer<typeof importOptionsSchema>;

// ============================================================================
// Import Preview Request
// ============================================================================

export const importPreviewRequestSchema = z.object({
  module: importModuleEnum,
  rows: z.array(z.record(z.any())),
  mappings: z.array(columnMappingSchema),
  limit: z.number().default(100), // Preview first N rows
});

export type ImportPreviewRequest = z.infer<typeof importPreviewRequestSchema>;

// ============================================================================
// Import Preview Response
// ============================================================================

export const importPreviewResponseSchema = z.object({
  results: z.array(importRowResultSchema),
  totalRows: z.number(),
  previewedRows: z.number(),
  validCount: z.number(),
  errorCount: z.number(),
});

export type ImportPreviewResponse = z.infer<typeof importPreviewResponseSchema>;

// ============================================================================
// Import Execute Request
// ============================================================================

export const importExecuteRequestSchema = z.object({
  module: importModuleEnum,
  rows: z.array(z.record(z.any())),
  mappings: z.array(columnMappingSchema),
  selectedIndices: z.array(z.number()).optional(), // If provided, only import these rows
  options: importOptionsSchema.optional(),
});

export type ImportExecuteRequest = z.infer<typeof importExecuteRequestSchema>;

// ============================================================================
// Import Execute Response
// ============================================================================

export const importExecuteResponseSchema = z.object({
  totalRows: z.number(),
  successCount: z.number(),
  errorCount: z.number(),
  skippedCount: z.number(),
  results: z.array(importRowResultSchema),
});

export type ImportExecuteResponse = z.infer<typeof importExecuteResponseSchema>;

// ============================================================================
// Get Fields Request
// ============================================================================

export const getImportFieldsRequestSchema = z.object({
  module: importModuleEnum,
});

export type GetImportFieldsRequest = z.infer<typeof getImportFieldsRequestSchema>;

// ============================================================================
// Get Fields Response
// ============================================================================

export const getImportFieldsResponseSchema = z.object({
  fields: z.array(importFieldSchema),
  sampleCsv: z.string().optional(), // Sample CSV content for download
});

export type GetImportFieldsResponse = z.infer<typeof getImportFieldsResponseSchema>;
